import React, { useLayoutEffect, useRef } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { m } from "framer-motion";
import '../styles/hero.css';

gsap.registerPlugin(ScrollTrigger);

const HeroSection = ({ onOpenSignup }) => {
  const scope = useRef(null);

  useLayoutEffect(() => {
    let ctx = gsap.context(() => {
      // Initial states for animations
      gsap.set(".animate-word", { opacity: 0, y: 10 });
      // Note: .hero-desc is intentionally NOT hidden here. It is the LCP element,
      // so it must paint immediately rather than wait for the entrance animation.
      gsap.set(".hero-buttons, .hero-stats", { opacity: 0, y: 20 });
      gsap.set(".hero-phone", { opacity: 0, scale: 0.9, x: 30 });
      gsap.set(".floating-pill", { opacity: 0, scale: 0.5 });

      const tl = gsap.timeline({
        scrollTrigger: {
          trigger: scope.current,
          start: "top 80%",
        }
      });

      tl.to(".animate-word", {
        opacity: 1,
        y: 0,
        duration: 0.4,
        stagger: 0.05,
        ease: "power2.out",
      })
      .to(".hero-phone", {
        opacity: 1,
        scale: 1,
        x: 0,
        duration: 0.8,
        ease: "power3.out"
      }, "-=0.2")
      .to(".hero-buttons, .hero-stats", {
        opacity: 1,
        y: 0,
        duration: 0.5,
        stagger: 0.1,
        ease: "power2.out"
      }, "-=0.5")
      .to(".floating-pill", {
        opacity: 1,
        scale: 1,
        duration: 0.5,
        stagger: 0.1,
        ease: "back.out(1.7)"
      }, "-=0.3");

    }, scope);

    return () => ctx.revert();
  }, []);

  const renderSplitText = (text, className = "") => {
    return text.split(" ").map((word, i) => (
      <span 
        key={i} 
        className={`animate-word ${className}`} 
        style={{ display: "inline-block", marginRight: "0.2em" }}
      >
        {word}
      </span>
    ));
  };

  return (
    <section className="hero" ref={scope}>
      <div className="hero-bg">
        <img src="/images/hero-bg.jpg" alt="Zambia background" width="1600" height="900" fetchPriority="high" decoding="async" />
        <div className="overlay1"></div>
        <div className="overlay2"></div>
      </div>
      
      <div className="hero-content">
        <div className="hero-text">
          <div className="hero-badge animate-word">
            <span className="dot"></span> Zambia's #1 Vehicle Marketplace
          </div>
          
          <h1>
            {renderSplitText("Buy & Sell Vehicles")} 
            <span className="green">{renderSplitText("Smarter, Safer")}</span>
            <span className="animate-word" style={{ margin: '0 8px' }}>&</span>
            <span className="orange">{renderSplitText("Faster")}</span> 
            <span>{renderSplitText("in Zambia")}</span>
          </h1>
          
          <p className="hero-desc">
            From Lusaka to Ndola, ZamWheelz connects serious buyers, trusted sellers, and professional dealers across Zambia. List your vehicle in minutes, find your perfect ride in seconds.
          </p>
          
          <div className="hero-buttons">
            <m.button
              className="btn-hero-primary"
              onClick={onOpenSignup}
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              🚀 Get Started Free
            </m.button>
            <m.a
              href="#how"
              className="btn-hero-secondary"
              whileHover={{ scale: 1.05, y: -2 }}
              whileTap={{ scale: 0.97 }}
              transition={{ type: "spring", stiffness: 400, damping: 17 }}
            >
              See How It Works
            </m.a>
          </div>
          
          <div className="hero-stats">
            {stats.map((s) => (
              <div className="hero-stat" key={s.label}>
                <strong>{s.value}</strong>
                <span>{s.label}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="hero-phone">
          <div className="phone-container">
            <div className="phone-glow"></div>
            <img className="phone-img" src="/images/mockup.png" alt="ZamWheelz App Mockup" width="420" height="536" decoding="async" />
            
            {[
              { icon: "✅", title: "Verified Seller", sub: "Lusaka", cls: "pill-1" },
              { icon: "🚗", title: "New Match!", sub: "Toyota Hilux", cls: "pill-2" },
              { icon: "💬", title: "Chat Started", sub: "Secure Messaging", cls: "pill-3" }
            ].map((p, idx) => (
              <div key={idx} className={`floating-pill ${p.cls}`}>
                <div className="pill-icon">{p.icon}</div>
                <div className="pill-text-content">
                  <span className="pill-title">{p.title}</span>
                  <span className="pill-sub">{p.sub}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

const stats = [
  { value: "20K+", label: "Active Listings" },
  { value: "15K+", label: "Happy Users" },
  { value: "8", label: "Categories" },
  { value: "4.8★", label: "Rating" },
];

export default HeroSection;